import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultGstExplainerData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'content',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'header-what-is-it',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is it?',
                variant: 'h3',
              },
            },
            {
              id: 'body-what-is-it',
              type: 'EditableEducationalSlideBody',
              props: {
                text: 'A separate 40% federal tax that applies when wealth is transferred to someone two or more generations younger than the giver, such as grandchildren. GST tax is meant to prevent families from "skipping" a generation to avoid taxation.\n\nIt applies **in addition** to gift or estate tax.',
              },
            },
            {
              id: 'header-when-applies',
              type: 'EducationalSlideHeading',
              props: {
                title: 'When does it apply?',
                variant: 'h3',
              },
            },
            {
              id: 'body-when-applies',
              type: 'EditableEducationalSlideBody',
              props: {
                text: '- Gifts or bequests to grandchildren (or more remote descendants)\n- Transfers to non-relatives more than 37.5 years younger than the donor (the person making the transfer/gift)\n- Distributions from a trust to skip persons',
              },
            },
            {
              id: 'header-terminology',
              type: 'EducationalSlideHeading',
              props: {
                title: 'Key terminology to know',
                variant: 'h3',
              },
            },
            {
              id: 'body-terminology',
              type: 'EditableEducationalSlideBody',
              props: {
                text: '- **Skip person:** A person who is at least two generations younger than the transferor (e.g., grandchildren), or a non-relative more than 37.5 years younger\n- **Direct skip:** Gift or bequest made directly to a skip person\n- **Taxable distribution:** Payment from a trust to a skip person\n- **Taxable termination:** Trust interest shifts from non-skip to skip person',
              },
            },
          ],
        },
      },
      {
        id: 'sidebar',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'header-minimize',
              type: 'EducationalSlideHeading',
              props: {
                title: 'How to minimize or manage GST tax',
                variant: 'h2',
              },
            },
            {
              id: 'item-minimize-1',
              type: 'PresentationListItem',
              props: {
                title: 'Make annual exclusion gifts',
                description:
                  'Up to $19,000 per person per year as of 2026 (GST-free if direct)',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-minimize-2',
              type: 'PresentationListItem',
              props: {
                title: 'Use & allocate GST exemption wisely',
                description:
                  'Use and allocate GST exemption wisely — especially when funding trusts',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-minimize-3',
              type: 'PresentationListItem',
              props: {
                title: 'Work with an advisor',
                description:
                  'Work with an advisor to structure GST-exempt dynasty trusts',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-minimize-4',
              type: 'PresentationListItem',
              props: {
                title: 'Avoid accidental GST events',
                description:
                  'Otherwise exempted scenarios may become GST events, such as accidental distributions to grandchildren from non-GST-exempt trusts',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'header-exemption',
              type: 'EducationalSlideHeading',
              props: {
                title: 'GST exemption',
                variant: 'h2',
              },
            },
            {
              id: 'item-exemption-1',
              type: 'PresentationListItem',
              props: {
                title: 'Each person has a lifetime GST exemption',
                description:
                  '$15 million in 2026 — same as estate & gift exemption',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-exemption-2',
              type: 'PresentationListItem',
              props: {
                title: 'Allocate exemption to shield gifts from tax',
                description:
                  'Exemption allocated properly to gifts made directly or to GST-eligible trusts can **benefit future generations free of GST tax**.',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
          ],
        },
      },
    ],
  };
}
